import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserPlus, Trash2, KeyRound, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);
const empty = { email: "", name: "", password: "", role: "admin" };

export default function UsersPage() {
  const { user: current } = useAuth();
  const canManage = current?.role === "owner";
  const { data: users = [], isLoading, error } = useSWR(canManage ? "/users" : null, fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState(null); // {user, password}

  if (!canManage) {
    return (
      <div className="max-w-md mx-auto text-center py-16 space-y-3">
        <ShieldCheck className="h-10 w-10 mx-auto text-muted-foreground" />
        <h2 className="font-display text-2xl font-medium">Akses terbatas</h2>
        <p className="text-sm text-muted-foreground">Hanya pemilik yang dapat mengelola akun pengguna.</p>
      </div>
    );
  }

  if (error) {
    return <div className="text-sm text-destructive">Gagal memuat data pengguna.</div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Akun dibuat");
      setForm(empty);
      setOpen(false);
      mutate("/users");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (u) => {
    if (!confirm(`Hapus akun ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Akun dihapus");
      mutate("/users");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.id}`, { role });
      toast.success("Role diperbarui");
      mutate("/users");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const resetPw = async () => {
    if (!pw?.user || !pw.password) return;
    try {
      await api.put(`/users/${pw.user.id}`, { password: pw.password });
      toast.success(`Password ${pw.user.email} direset`);
      setPw(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Manajemen Akses</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Pengguna</h1>
          <p className="text-sm text-muted-foreground mt-1">Kelola akun pemilik & admin pengelola.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="gap-2" data-testid="add-user-button">
          <UserPlus className="h-4 w-4" /> Tambah Pengguna
        </Button>
      </header>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Memuat...</div>
      ) : users.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 flex flex-col items-center text-center gap-4">
            <div className="h-14 w-14 rounded-md bg-primary/10 text-primary grid place-items-center">
              <Users className="h-7 w-7" />
            </div>
            <div className="font-display text-lg font-medium">Belum ada pengguna</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {users.map((u) => (
            <Card key={u.id} data-testid={`user-card-${u.id}`}>
              <CardContent className="p-4 sm:p-5 flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary grid place-items-center font-display font-medium">
                  {u.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{u.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">Dibuat {formatDate(u.created_at)}</div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.id === current.id}>
                    <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="owner">Owner</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPw({ user: u, password: "" })} title="Reset password" data-testid={`reset-pw-${u.id}`}>
                      <KeyRound className="h-4 w-4" />
                    </Button>
                    {u.id !== current.id && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => del(u)} data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Tambah Pengguna</DialogTitle>
            <DialogDescription>Buat akun untuk admin pengelola atau pemilik tambahan.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nama Lengkap</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Password</Label>
                <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="user-save-button">
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pw} onOpenChange={(v) => !v && setPw(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>{pw?.user?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Password Baru</Label>
            <Input type="password" minLength={6} value={pw?.password || ""} onChange={(e) => setPw({ ...pw, password: e.target.value })} data-testid="reset-password-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPw(null)}>Batal</Button>
            <Button onClick={resetPw} data-testid="reset-password-submit">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
